import React, { useEffect, useState } from "react";
import { Modal, Typography, Button } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router";
import { getProfileCached } from "../../../hooks/useClientData";
import { getUroSocket } from "../../../services/URO";
import getRoutes from "../appRoutes";
import { AppNavbar, NAVBAR_HEIGHT } from "./components/AppNavbar";
import BottomNavBar from "./components/BottomNavBar";
import { CreateModal } from "../../../modals";
import useResponsive from "../../../utils/useResponsive";

function AppLayout(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const routes = getRoutes();
  const { isMobile, showBottomNav, showSidebar } = useResponsive();

  // New-design screens handle their own sidebar/topbar — hide the old AppNavbar for them
  const NEW_DESIGN_PATHS = ['/explore', '/dashboard', '/zen', '/gallery', '/account', '/chat', '/docs', '/llm-playground', '/studios', '/brand-dna', '/agent'];
  const isNewDesign = NEW_DESIGN_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  const [balance, setBalance] = useState(0);
  const [frozenBalance, setFrozenBalance] = useState(0);
  const [helpModalVisible, setHelpModalVisible] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [initialCategory, setInitialCategory] = useState(null);

  // Get current active tab from path for bottom nav
  const getActiveTab = () => {
    if (location.pathname.startsWith("/community")) return "community";
    if (location.pathname.startsWith("/gallery")) return "gallery";
    if (location.pathname.startsWith("/account")) return "profile";
    return "explore";
  };

  const handleTabChange = (tab) => {
    const paths = {
      explore: "/explore",
      community: "/community",
      gallery: "/gallery",
      profile: "/account",
    };
    navigate(paths[tab]);
  };

  const handleCreatePress = () => {
    setInitialCategory(null);
    setIsCreateModalOpen(true);
  };

  const openCreateModalWithCategory = (categorySlug) => {
    setInitialCategory(categorySlug);
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setInitialCategory(null);
  };

  const handleSelectItem = (item) => {
    setIsCreateModalOpen(false);
    const id =
      item.item_type === "model" ? item.model_id : item.workflow_definition_id;
    navigate(`/create/${id}`);
  };

  useEffect(() => {
    let socket;
    let onBalanceChanged;
    async function fetchBalanceAndSubscribe() {
      try {
        const res = await getProfileCached();
        setBalance(res.data?.client_balance ?? 0);
        setFrozenBalance(res.data?.client_frozen_balance ?? 0);
      } catch {
        setBalance(0);
        setFrozenBalance(0);
      }
      socket = getUroSocket();
      // Ensure socket is connected at the layout level so all pages receive real-time updates
      if (socket && socket.connect) socket.connect();
      onBalanceChanged = (data) => {
        setBalance(
          data?.client_balance?.client_balance || data?.client_balance || 0,
        );
        setFrozenBalance(
          data?.client_balance?.client_frozen_balance ||
            data?.client_frozen_balance ||
            0,
        );
      };
      socket.on("client:balance_changed", onBalanceChanged);
    }
    fetchBalanceAndSubscribe();
    return () => {
      if (socket) {
        if (socket.off && onBalanceChanged) {
          socket.off("client:balance_changed", onBalanceChanged);
        }
        if (socket.disconnect) socket.disconnect();
      }
    };
  }, []);

  // Freeze page-level scroll on playground routes; restore on others
  useEffect(() => {
    const isPlayground = location.pathname.includes("playground");
    const prevHtmlOverflow = document.documentElement.style.overflowY;
    const prevBodyOverflow = document.body.style.overflowY;
    if (isPlayground) {
      document.documentElement.style.overflowY = "hidden";
      document.body.style.overflowY = "hidden";
    } else {
      document.documentElement.style.overflowY = "";
      document.body.style.overflowY = "";
    }
    return () => {
      document.documentElement.style.overflowY = prevHtmlOverflow;
      document.body.style.overflowY = prevBodyOverflow;
    };
  }, [location.pathname]);

  // Redirect playground routes to zen on mobile
  useEffect(() => {
    if (
      isMobile &&
      (location.pathname === "/playground" ||
        location.pathname === "/workflow-playground")
    ) {
      navigate("/zen", { replace: true });
    }
  }, [isMobile, location.pathname, navigate]);

  return (
    <div
      style={{
        height: "100dvh",
        width: "100%",
        background: '#050505',
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* CSS Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>

      {/* Top navbar - only on tablet/desktop, hidden for new-design screens */}
      {showSidebar && !isNewDesign && (
        <AppNavbar
          routes={routes}
          pathname={location.pathname}
          onNavigate={navigate}
          balance={balance}
          onTopUp={() => navigate("/account")}
          onHelp={() => setHelpModalVisible(true)}
        />
      )}

      {/* Main content wrapper */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          marginTop: showSidebar && !isNewDesign ? NAVBAR_HEIGHT : 0,
          backgroundColor: '#050505',
        }}
      >
        <main
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: '#050505',
            color: '#FFFFFF',
            overflow: "hidden",
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflowX: "hidden",
              overflowY: location.pathname.includes("playground")
                ? "hidden"
                : "auto",
              overscrollBehavior: location.pathname.includes("playground")
                ? "none"
                : undefined,
              WebkitOverflowScrolling: "touch",
              padding: 0,
            }}
            className="hide-scrollbar"
          >
            <Outlet
              context={{
                balance,
                frozenBalance,
                openCreateModal: handleCreatePress,
                openCreateModalWithCategory,
              }}
            />
          </div>
        </main>

        {/* Bottom Navigation Bar - only on mobile */}
        {showBottomNav && (
          <BottomNavBar
            activeTab={getActiveTab()}
            onTabChange={handleTabChange}
            onCreatePress={handleCreatePress}
          />
        )}
      </div>

      {/* Create Modal */}
      <CreateModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSelectItem={handleSelectItem}
        initialCategory={initialCategory}
      />

      {/* Help Modal */}
      <Modal
        title={null}
        open={helpModalVisible}
        onCancel={() => setHelpModalVisible(false)}
        footer={null}
        centered
        width={400}
        styles={{
          content: {
            background: '#0d0d0d',
            border: `1px solid rgba(255,255,255,0.1)`,
            borderRadius: 16,
            overflow: "hidden",
          },
        }}
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🆘</div>
            <Typography.Title
              level={3}
              style={{ color: '#FFFFFF', margin: 0, marginBottom: 8 }}
            >
              Need Help?
            </Typography.Title>
            <Typography.Text
              style={{ color: '#94A3B8', fontSize: 14 }}
            >
              Join our Discord community for support, tips, and updates
            </Typography.Text>
          </div>

          <div
            style={{
              background: "rgba(88, 101, 242, 0.1)",
              border: "1px solid rgba(88, 101, 242, 0.3)",
              borderRadius: 12,
              padding: 20,
              marginBottom: 24,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 24 }}>💬</div>
              <Typography.Text
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#FFFFFF',
                }}
              >
                Discord Community
              </Typography.Text>
            </div>
            <Typography.Text
              style={{
                color: '#94A3B8',
                fontSize: 13,
                display: "block",
                marginBottom: 16,
              }}
            >
              Get instant help from our community and team.
            </Typography.Text>

            <Button
              type="primary"
              size="large"
              onClick={() => {
                window.open("https://discord.gg/4b6S849Y2t", "_blank");
                setHelpModalVisible(false);
              }}
              style={{
                background: "linear-gradient(135deg, #5865f2, #7289da)",
                border: "none",
                borderRadius: 8,
                fontWeight: 600,
                height: 44,
                width: "100%",
              }}
            >
              🚀 Join Discord Server
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AppLayout;
